// companyNavigationApi.js

import { customBaseQuery } from "@/config/customBaseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";

export const companyNavigationApi = createApi({
    reducerPath: "companyNavigationApi",

    baseQuery: customBaseQuery,

    tagTypes: [
        "CompanyBaseNavigation",
        "CompanyBaseNavigationDropdown",
        "CompanyNavigation",
        "CompanyNavigationDropdown",
        "CompanySubNavigation",
        "CompanyNavVisibility",
    ],

    endpoints: (builder) => ({

        // =========================================================
        // GET Company Base Navigation List
        // =========================================================
        getCompanyBaseNavigationList: builder.query({
            query: ({
                iPageNo = 1,
                iPageRecords = 10,
                AccountTypeId,
                CompanyId,
                SearchKeyword = "",
            }) => ({
                url: "/v1/CompanyNavigationSetup/GetCompanyBaseNavigationList",
                method: "GET",
                params: {
                    iPageNo,
                    iPageRecords,
                    AccountTypeId,
                    CompanyId,
                    SearchKeyword,
                },
            }),

            transformResponse: (response) => {
                return {
                    baseNavigationlist: response?.data ?? [],
                    pagination: response?.pagination ?? {},
                };
            },

            providesTags: ["CompanyBaseNavigation"],

            keepUnusedDataFor: 5 * 60,
        }),

        // =========================================================
        // INSERT Company Base Navigation
        // =========================================================
        insertCompanyBaseNavigation: builder.mutation({
            query: ({ CompanyId, formData }) => ({
                url: `/v1/CompanyNavigationSetup/CompanyBaseNavigationInsert?CompanyId=${CompanyId}`,
                method: "POST",
                body: formData,
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanyBaseNavigation",
                        "CompanyBaseNavigationDropdown",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),

        // =========================================================
        // UPDATE Company Base Navigation
        // =========================================================
        updateCompanyBaseNavigation: builder.mutation({
            query: ({ CompanyId, formData }) => ({
                url: `/v1/CompanyNavigationSetup/CompanyBaseNavigationUpdate?CompanyId=${CompanyId}`,
                method: "POST",
                body: formData,
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanyBaseNavigation",
                        "CompanyBaseNavigationDropdown",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),

        // =========================================================
        // DELETE Company Base Navigation
        // =========================================================
        deleteCompanyBaseNavigation: builder.mutation({
            query: ({ CompanyId, BaseNavigationId, DeletedBy }) => ({
                url: `/v1/CompanyNavigationSetup/DeleteCompanyBaseNavigation?CompanyId=${CompanyId}`,
                method: "POST",
                body: {
                    BaseNavigationId,
                    DeletedBy,
                },
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanyBaseNavigation",
                        "CompanyBaseNavigationDropdown",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),

        // =========================================================
        // GET Company Navigation List
        // =========================================================
        getCompanyNavigationList: builder.query({
            query: ({
                iPageNo = 1,
                iPageRecords = 10,
                AccountTypeId,
                CompanyId,
                BaseNavigationId,
                SearchKeyword = ""
            }) => ({
                url: "/v1/CompanyNavigationSetup/GetCompanyNavigationList",
                method: "GET",
                params: {
                    iPageNo,
                    iPageRecords,
                    AccountTypeId,
                    CompanyId,
                    BaseNavigationId,
                    SearchKeyword
                }
            }),
            transformResponse: (response) => {
                return {
                    navigationList: response?.data ?? [],
                    pagination: response?.pagination ?? {},
                };
            },

            providesTags: ["CompanyNavigation"],

            keepUnusedDataFor: 5 * 60,
        }),

        // =========================================================
        // INSERT Company Navigation
        // =========================================================
        insertCompanyNavigation: builder.mutation({
            query: ({
                CompanyId,
                formData,
            }) => ({
                url: `/v1/CompanyNavigationSetup/CompanyNavigationInsert?CompanyId=${CompanyId}`,
                method: "POST",
                body: formData,
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanyNavigation",
                        "CompanyNavigationDropdown",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),


        // =========================================================
        // UPDATE Company Navigation
        // =========================================================
        updateCompanyNavigation: builder.mutation({
            query: ({
                CompanyId,
                formData,
            }) => ({
                url: `/v1/CompanyNavigationSetup/CompanyNavigationUpdate?CompanyId=${CompanyId}`,
                method: "POST",
                body: formData,
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanyNavigation",
                        "CompanyNavigationDropdown",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),


        // =========================================================
        // DELETE Company Navigation
        // =========================================================
        deleteCompanyNavigation: builder.mutation({
            query: ({
                CompanyId,
                NavigationId,
                DeletedBy,
            }) => ({
                url: `/v1/CompanyNavigationSetup/DeleteCompanyNavigation?CompanyId=${CompanyId}`,
                method: "POST",
                body: {
                    NavigationId,
                    DeletedBy,
                },
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanyNavigation",
                        "CompanyNavigationDropdown",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),

        // =========================================================
        // GET Company Sub Navigation List
        // =========================================================

        getCompanySubNavigatinList: builder.query({
            query: ({
                iPageNo = 1,
                iPageRecords = 10,
                SearchKeyword,
                AccountTypeId,
                NavigationId,
                CompanyId
            }) => ({
                url: "/v1/CompanyNavigationSetup/GetCompanySubNavigationList",
                method: "GET",
                params: {
                    iPageNo,
                    iPageRecords,
                    SearchKeyword,
                    AccountTypeId,
                    NavigationId,
                    CompanyId
                }
            }),

            transformResponse: (response) => ({
                subNavigationList: response?.data ?? [],
                pagination: response?.pagination ?? {}
            }),

            providesTags: ["CompanySubNavigation"],

            keepUnusedDataFor: 5 * 60,
        }),

        // =========================================================
        // INSERT Company Sub Navigation
        // =========================================================

        insertCompanySubNavigation: builder.mutation({
            query: ({ CompanyId, formData }) => ({
                url: `/v1/CompanyNavigationSetup/CompanySubNavigationInsert?CompanyId=${CompanyId}`,
                method: "POST",
                body: formData,
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanySubNavigation",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),

        // =========================================================
        // UPDATE Company Sub Navigation
        // =========================================================
        updateCompanySubNavigation: builder.mutation({
            query: ({ CompanyId, formData }) => ({
                url: `/v1/CompanyNavigationSetup/CompanySubNavigationUpdate?CompanyId=${CompanyId}`,
                method: "POST",
                body: formData
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanySubNavigation",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),

        // =========================================================
        // DELETE Company Sub Navigation
        // =========================================================
        deleteCompanySubNavigation: builder.mutation({
            query: ({ CompanyId, SubNavigationId, DeletedBy }) => ({
                url: `/v1/CompanyNavigationSetup/DeleteCompanySubNavigation?CompanyId=${CompanyId}`,
                method: "POST",
                body: {
                    SubNavigationId,
                    DeletedBy,
                },
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanySubNavigation",
                        { type: "CompanyNavVisibility", id: CompanyId },
                    ],
        }),

        // =========================================================
        // GET Company Base Navigation Dropdown List
        // =========================================================
        getCompanyBaseNavigationDropdownList: builder.query({
            query: ({ CompanyId }) => ({
                url: "/v1/CompanyNavigationSetup/GetCompanyBaseNavigationDropdownList",
                method: "GET",
                params: {
                    CompanyId,
                },
            }),

            transformResponse: (response) => {
                return response?.data?.baseNavigationList ?? [];
            },

            providesTags: ["CompanyBaseNavigationDropdown"],

            keepUnusedDataFor: 5 * 60,
        }),


        // =========================================================
        // GET Company Navigation Dropdown List
        // =========================================================
        getCompanyNavigationDropdownList: builder.query({
            query: ({
                CompanyId,
                BaseNavigationId,
            }) => ({
                url: "/v1/CompanyNavigationSetup/GetCompanyNavigationDropdownList",
                method: "GET",
                params: {
                    CompanyId,
                    BaseNavigationId,
                },
            }),

            transformResponse: (response) => {
                return response?.data?.navigationList ?? [];
            },

            providesTags: ["CompanyNavigationDropdown"],

            keepUnusedDataFor: 5 * 60,
        }),

        // =========================================================
        // GET Company Navigation Visibility List
        // =========================================================
        getCompanyNavigationVisibilityList: builder.query({
            query: ({ CompanyId, RoleId }) => ({
                url: "/v1/CompanyNavigationSetup/GetCompanyNavigatioVisibilityList",
                method: "GET",
                params: {
                    CompanyId,
                    RoleId,
                },
            }),

            transformResponse: (response) => ({
                visibilityList: response?.data ?? [],
                ...response,
            }),

            providesTags: (result, error, { CompanyId }) => [
                { type: "CompanyNavVisibility", id: CompanyId },
            ],

            // Ensures the list is refetched on mount even if it was
            // invalidated while this component was unmounted (e.g. user
            // edited Base Navigation on another page, then navigated here).
            refetchOnMountOrArgChange: true,

            keepUnusedDataFor: 5 * 60,
        }),

        // =========================================================
        // PROCESS Company Navigation Visibility
        // =========================================================
        companyNavigationVisibilityProcess: builder.mutation({
            query: ({
                CompanyId,
                RoleId,
                JsonStringValue,
                NavigationVisibilityProcess,
            }) => {
                const formData = new FormData();

                formData.append("CompanyId", String(CompanyId ?? 0));
                formData.append("RoleId", String(RoleId ?? 0));
                formData.append(
                    "JsonStringValue",
                    JsonStringValue ?? ""
                );
                formData.append(
                    "NavigationVisibilityProcess",
                    NavigationVisibilityProcess ?? "{}"
                );

                return {
                    url: "/v1/CompanyNavigationSetup/CompanyNavigationVisibilityProcess",
                    method: "POST",
                    body: formData,
                };
            },

            invalidatesTags: (result, error, { CompanyId }) =>
                error ? [] : [{ type: "CompanyNavVisibility", id: CompanyId }],
        }),
        // =========================================================
        // PULL NAVIGATION FROM MASTER
        // Sync newly added Master Navigation to Company Navigation
        // =========================================================
        pullNavigationFromMaster: builder.mutation({
            query: ({ CompanyId }) => ({
                url: "/v1/CompanyNavigationSetup/PullNavigationFromMaster",
                method: "POST",
                params: {
                    CompanyId,
                },
            }),

            invalidatesTags: (result, error, { CompanyId }) =>
                error
                    ? []
                    : [
                        "CompanyBaseNavigation",
                        "CompanyBaseNavigationDropdown",
                        "CompanyNavigation",
                        "CompanyNavigationDropdown",
                        "CompanySubNavigation",
                        {
                            type: "CompanyNavVisibility",
                            id: CompanyId,
                        },
                    ],
        }),
    }),
});

export const {
    // Base Navigation
    useGetCompanyBaseNavigationListQuery,
    useInsertCompanyBaseNavigationMutation,
    useUpdateCompanyBaseNavigationMutation,
    useDeleteCompanyBaseNavigationMutation,

    // Navigation
    useGetCompanyNavigationListQuery,
    useInsertCompanyNavigationMutation,
    useUpdateCompanyNavigationMutation,
    useDeleteCompanyNavigationMutation,

    // Sub Navigation
    useGetCompanySubNavigatinListQuery,
    useInsertCompanySubNavigationMutation,
    useUpdateCompanySubNavigationMutation,
    useDeleteCompanySubNavigationMutation,

    // Pull Navigation From Master
    usePullNavigationFromMasterMutation,


    // dropdown
    useGetCompanyBaseNavigationDropdownListQuery,
    useGetCompanyNavigationDropdownListQuery,

    // Navigation Visibility
    useGetCompanyNavigationVisibilityListQuery,
    useCompanyNavigationVisibilityProcessMutation,
} = companyNavigationApi;